import React from "react";
import PropTypes from "prop-types";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

/**
 * Provides DndContext + DragOverlay for dashboard global-parameter sortable bars.
 */
export default function DashboardGridDndShell({ dragEnabled, children, onDragStart, onDragOver, onDragCancel, onDragEnd }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 3 } }));
  const [overlayLabel, setOverlayLabel] = React.useState(null);
  const collisionDetection = React.useCallback(args => {
    // Prefer strict pointer hit first (most natural for cross-bar drop),
    // then fall back to geometric matching for edge cases.
    const pointerHits = pointerWithin(args);
    if (pointerHits.length > 0) {
      return pointerHits;
    }
    const intersections = rectIntersection(args);
    if (intersections.length > 0) {
      return intersections;
    }
    return closestCorners(args);
  }, []);

  const handleDragStart = React.useCallback(
    event => {
      const { active } = event;
      setOverlayLabel(typeof active?.data?.current?.label === "string" ? active.data.current.label : null);
      onDragStart(event);
    },
    [onDragStart]
  );

  const handleDragOver = React.useCallback(
    event => {
      onDragOver(event);
    },
    [onDragOver]
  );

  const handleDragEnd = React.useCallback(
    event => {
      setOverlayLabel(null);
      if (dragEnabled && onDragEnd) {
        onDragEnd(event);
      }
    },
    [dragEnabled, onDragEnd]
  );

  const handleDragCancel = React.useCallback(event => {
    setOverlayLabel(null);
    onDragCancel(event);
  }, [onDragCancel]);

  if (!dragEnabled) {
    return <>{children}</>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}>
      {children}
      <DragOverlay zIndex={2000}>
        {overlayLabel ? (
          <div className="parameter-block dashboard-param-dnd-overlay di-block bg-white tiled p-x-15 p-y-10">
            <div className="parameter-heading">
              <label>{overlayLabel}</label>
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

DashboardGridDndShell.propTypes = {
  dragEnabled: PropTypes.bool,
  children: PropTypes.node,
  onDragStart: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragCancel: PropTypes.func,
  onDragEnd: PropTypes.func,
};

DashboardGridDndShell.defaultProps = {
  dragEnabled: false,
  children: null,
  onDragStart: () => {},
  onDragOver: () => {},
  onDragCancel: () => {},
  onDragEnd: () => {},
};
